import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ApartmentOutlined,
  DeleteOutlineRounded,
  LockOutlined,
} from "@mui/icons-material";
import color from "../../../Constants/colors";
import {
  DocumentCraftingDocumentDetail,
  DocumentCraftingPermission,
  DocumentCraftingShares,
  DocumentCraftingVisibility,
} from "../../../Models/documentCrafting";
import {
  useDocumentCraftingShares,
  useUpdateDocumentCraftingDocument,
  useUpdateDocumentCraftingShares,
} from "../../../Query/useDocumentCrafting";
import { ErrorState, LoadingState } from "../Common/DocumentCraftingUi";
import { useUserName } from "../Common/format";
import {
  enumValues,
  useDocumentCraftingEnumLabels,
} from "../Common/useDocumentCraftingEnumLabels";
import {
  ShareDraft,
  addShare,
  hasShareChanges,
  initShareDraft,
  removeShare,
  setSharePermission,
  setVisibility,
  setWorkspacePermission,
  shareChanges,
} from "./shareModel";
import MemberSearch from "./MemberSearch";

const PERMISSIONS = enumValues<DocumentCraftingPermission>(
  DocumentCraftingPermission,
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{ fontSize: 13, fontWeight: 700, color: color.gray700, mb: 1, mt: 2.5 }}
  >
    {children}
  </Typography>
);

const PermissionSelect: React.FC<{
  value: DocumentCraftingPermission;
  label: string;
  disabled?: boolean;
  onChange: (permission: DocumentCraftingPermission) => void;
}> = ({ value, label, disabled, onChange }) => {
  const labels = useDocumentCraftingEnumLabels();
  return (
    <TextField
      select
      size="small"
      value={value}
      disabled={disabled}
      onChange={(event) =>
        onChange(Number(event.target.value) as DocumentCraftingPermission)
      }
      inputProps={{ "aria-label": label }}
      sx={{ minWidth: 120 }}
    >
      {PERMISSIONS.map((permission) => (
        <MenuItem key={permission} value={permission}>
          {labels.permission(permission)}
        </MenuItem>
      ))}
    </TextField>
  );
};

interface FormProps {
  document: DocumentCraftingDocumentDetail;
  shares: DocumentCraftingShares;
  onClose: () => void;
  onTransfer?: () => void;
}

// Mounted once per opening with the loaded shares, so it starts clean.
const ShareForm: React.FC<FormProps> = ({
  document,
  shares,
  onClose,
  onTransfer,
}) => {
  const { t } = useTranslation("documentCrafting");
  const labels = useDocumentCraftingEnumLabels();
  const userName = useUserName();
  const [initial] = useState<ShareDraft>(() => initShareDraft(shares));
  const [draft, setDraft] = useState<ShareDraft>(initial);
  const updateShares = useUpdateDocumentCraftingShares();
  const updateDocument = useUpdateDocumentCraftingDocument();

  const ownerId = document.owner?.id ?? null;
  const changes = shareChanges(initial, draft);
  const pending = updateShares.isPending || updateDocument.isPending;

  const save = async () => {
    try {
      if (changes.shares) {
        await updateShares.mutateAsync({
          documentId: document.id,
          data: changes.shares,
        });
      }
      if (changes.document) {
        await updateDocument.mutateAsync({
          documentId: document.id,
          data: changes.document,
        });
      }
      onClose();
    } catch {
      // The request shows its error snack; the dialog keeps the changes.
    }
  };

  const visibilityOption = (
    value: DocumentCraftingVisibility,
    Icon: typeof LockOutlined,
    hint: string,
  ) => (
    <FormControlLabel
      value={value}
      control={<Radio size="small" />}
      disabled={pending}
      label={
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, py: 0.5 }}>
          <Icon sx={{ fontSize: 18, color: color.gray500, mt: "2px" }} />
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: color.gray900 }}>
              {labels.visibility(value)}
            </Typography>
            <Typography sx={{ fontSize: 12, color: color.gray500 }}>
              {hint}
            </Typography>
          </Box>
        </Box>
      }
      sx={{ alignItems: "flex-start", mr: 0 }}
    />
  );

  return (
    <>
      <DialogContent>
        <SectionTitle>{t("detail.share.visibility")}</SectionTitle>
        <RadioGroup
          value={draft.visibility}
          onChange={(event) =>
            setDraft(
              setVisibility(
                draft,
                Number(event.target.value) as DocumentCraftingVisibility,
              ),
            )
          }
        >
          {visibilityOption(
            DocumentCraftingVisibility.PRIVATE,
            LockOutlined,
            t("detail.share.privateHint"),
          )}
          {visibilityOption(
            DocumentCraftingVisibility.WORKSPACE,
            ApartmentOutlined,
            t("detail.share.workspaceHint"),
          )}
        </RadioGroup>
        {draft.visibility === DocumentCraftingVisibility.WORKSPACE && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              ml: 4,
              mt: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography sx={{ fontSize: 13, color: color.gray700 }}>
              {t("detail.share.workspacePermission")}
            </Typography>
            <PermissionSelect
              value={draft.workspacePermission}
              label={t("detail.share.workspacePermission")}
              disabled={pending}
              onChange={(permission) =>
                setDraft(setWorkspacePermission(draft, permission))
              }
            />
          </Box>
        )}

        <SectionTitle>{t("detail.share.people")}</SectionTitle>
        <MemberSearch
          label={t("detail.share.addPerson")}
          excludeIds={[
            ...(ownerId !== null ? [ownerId] : []),
            ...draft.shares.map((share) => share.user.id),
          ]}
          onSelect={(user) => setDraft(addShare(draft, user, ownerId))}
          disabled={pending}
        />
        <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, mt: 1 }}>
          {draft.shares.length === 0 && (
            <Typography
              component="li"
              sx={{ fontSize: 13, color: color.gray500, py: 1 }}
            >
              {t("detail.share.noShares")}
            </Typography>
          )}
          {draft.shares.map((share) => {
            const name = userName(share.user);
            return (
              <Box
                component="li"
                key={share.user.id}
                data-testid={`share-${share.user.id}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  py: 1,
                  borderBottom: `1px solid ${color.gray100}`,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    noWrap
                    sx={{ fontSize: 14, fontWeight: 600, color: color.gray900 }}
                  >
                    {name}
                  </Typography>
                  <Typography noWrap sx={{ fontSize: 12, color: color.gray500 }}>
                    {share.user.email}
                  </Typography>
                </Box>
                <PermissionSelect
                  value={share.permission}
                  label={t("detail.share.permissionFor", { name })}
                  disabled={pending}
                  onChange={(permission) =>
                    setDraft(setSharePermission(draft, share.user.id, permission))
                  }
                />
                <Tooltip title={t("detail.share.remove", { name })} arrow>
                  <span>
                    <IconButton
                      size="small"
                      disabled={pending}
                      onClick={() => setDraft(removeShare(draft, share.user.id))}
                      aria-label={t("detail.share.remove", { name })}
                    >
                      <DeleteOutlineRounded fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            );
          })}
        </Box>

        <SectionTitle>{t("detail.share.owner")}</SectionTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: 14, color: color.gray900, flex: 1 }}>
            {userName(document.owner)}
            {document.owner && (
              <Typography component="span" sx={{ fontSize: 12, color: color.gray500, ml: 1 }}>
                {document.owner.email}
              </Typography>
            )}
          </Typography>
          {onTransfer && (
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={onTransfer}
              disabled={pending}
            >
              {t("detail.transfer.title")}
            </Button>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="secondary" onClick={onClose} disabled={pending}>
          {t("common.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={!hasShareChanges(changes) || pending}
        >
          {t("common.save")}
        </Button>
      </DialogActions>
    </>
  );
};

interface Props {
  open: boolean;
  document: DocumentCraftingDocumentDetail;
  onClose: () => void;
  /** Opens the ownership transfer (owner and admins). */
  onTransfer?: () => void;
}

/**
 * Sharing of a document: private or the whole workspace (with a level), plus
 * explicit shares with members, each with view or edit rights.
 */
const ShareDialog: React.FC<Props> = ({ open, document, onClose, onTransfer }) => {
  const { t } = useTranslation("documentCrafting");
  const shares = useDocumentCraftingShares(document.id, open);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ overflowWrap: "anywhere" }}>
        {t("detail.share.title", { title: document.title })}
      </DialogTitle>
      {shares.data ? (
        <ShareForm
          key={document.id}
          document={document}
          shares={shares.data}
          onClose={onClose}
          onTransfer={onTransfer}
        />
      ) : (
        <>
          <DialogContent>
            {shares.isError ? (
              <ErrorState
                onRetry={() => shares.refetch()}
                text={t("detail.share.loadError")}
              />
            ) : (
              <LoadingState />
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button color="secondary" onClick={onClose}>
              {t("common.close")}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default ShareDialog;
